import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  Avatar, Chip, Divider, List, ListItem, ListItemText, ListItemAvatar,
  TextField, Button, LinearProgress, Skeleton, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  People, CheckCircle, Cancel, HourglassEmpty, BeachAccess,
  Business, CalendarMonth, ArrowForward, Refresh,
  AccessTime, EventBusy, AttachMoney, DirectionsBus,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const fmtINR = (n) => {
  const num = Number(n || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const StatCard = ({ title, value, icon, gradient, sub, onClick, loading }) => (
  <Card onClick={onClick} sx={{
    borderRadius: 3, cursor: onClick ? 'pointer' : 'default',
    background: gradient,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    transition: 'all 0.25s ease',
    overflow: 'hidden', position: 'relative',
    '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' } : {},
  }}>
    <Box sx={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', top: -30, right: -30 }} />
    <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: -20, left: -20 }} />
    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: 13, mb: 0.5 }}>{title}</Typography>
          {loading ? (
            <Skeleton variant="text" width={60} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          ) : (
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.1 }}>{value ?? '—'}</Typography>
          )}
          {sub && <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Avatar sx={{ background: 'rgba(255,255,255,0.2)', width: 52, height: 52, backdropFilter: 'blur(8px)' }}>
          {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 26 } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const DeptBar = ({ std, sec, present, absent, leave, total }) => {
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;
  const color = pct >= 80 ? '#2e7d32' : pct >= 50 ? '#f57c00' : '#c62828';
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fafafa', border: '1px solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <Typography fontSize={13} fontWeight={700}>{std} - {sec}</Typography>
        </Box>
        <Typography fontSize={12} fontWeight={800} color={color}>{pct}% · {total} total</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 4, background: '#e8eaf6', mb: 1,
        '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${color}99,${color})`, borderRadius: 4 },
      }} />
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {[
          { label: `Present ${present}`,  bg: '#e8f5e9', color: '#2e7d32' },
          { label: `Absent ${absent}`,    bg: '#ffebee', color: '#c62828' },
          { label: `Leave ${leave}`,      bg: '#fff3e0', color: '#e65100' },
        ].map(s => (
          <Chip key={s.label} label={s.label} size="small"
            sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: 10, height: 20 }} />
        ))}
      </Box>
    </Box>
  );
};

const FeesBar = ({ std, sec, totalFees, totalFeesPaid, totalFeesBalance, students }) => {
  const hasStructure = totalFees > 0;
  const pct = hasStructure ? Math.round((totalFeesPaid / totalFees) * 100) : 0;
  const color = !hasStructure ? '#9e9e9e' : pct >= 80 ? '#2e7d32' : pct >= 50 ? '#f57c00' : '#c62828';
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fafafa', border: '1px solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <Typography fontSize={13} fontWeight={700}>{std} - {sec}</Typography>
        </Box>
        <Typography fontSize={12} fontWeight={800} color={color}>
          {hasStructure ? `${pct}% · ${students} students` : 'No structure'}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 4, background: '#e8eaf6', mb: 1,
        '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${color}99,${color})`, borderRadius: 4 },
      }} />
      {hasStructure ? (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[
            { label: `Total ${fmtINR(totalFees)}`,          bg: '#e8eaf6', color: '#1a237e' },
            { label: `Paid ${fmtINR(totalFeesPaid)}`,       bg: '#e8f5e9', color: '#2e7d32' },
            { label: `Pending ${fmtINR(totalFeesBalance)}`, bg: '#ffebee', color: '#c62828' },
          ].map(s => (
            <Chip key={s.label} label={s.label} size="small"
              sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: 10, height: 20 }} />
          ))}
        </Box>
      ) : (
        <Typography fontSize={11} color="text.disabled">Set up fee structure for this class</Typography>
      )}
    </Box>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [statsLoading, setStatsLoading] = useState(true);
  const [deptLoading, setDeptLoading] = useState(true);
  const [sideLoading, setSideLoading] = useState(true);

  // ── pageReady: true only when ALL initial data has loaded ─────────────────
  const [pageReady, setPageReady] = useState(false);

  const [stats, setStats] = useState({ present: 0, absent: 0, leave: 0, total: 0 });
  const [depts, setDepts] = useState([]);
  const [deptStats, setDeptStats] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [feesStats, setFeesStats] = useState([]);
  const [feesLoading, setFeesLoading] = useState(true);
  const [currentAcYear, setCurrentAcYear] = useState('');
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalFeesPaid, setTotalFeesPaid] = useState(0);
  const [totalFeesAmount, setTotalFeesAmount] = useState(0);

  // ── Load ALL dashboard data in one go ─────────────────────────────────────
  // Replaced separate useEffects — page stays blank until everything is ready
  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      setPageReady(false);
      try {
        // Step 1: Load departments + academic year + vehicles in parallel
        const [secData, acData, vehicleData, leavesData, holidayData] = await Promise.all([
          InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
          InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
          user?.role !== 'Teacher'
            ? InvokeApi(Config.main_url + apiList.getVehicles, {})
            : Promise.resolve(null),
          InvokeApi(Config.main_url + apiList.getLeavesStudents, { status: 'Pending' }),
          InvokeApi(Config.main_url + apiList.getHolidays, {}),
        ]);

        if (cancelled) return;

        const sections = secData?.sections || [];
        const years    = acData?.academic_years || [];
        const current  = years.find(y => y.is_current) || years[0];
        const acYear   = current?.value || '';

        setDepts(sections);
        setCurrentAcYear(acYear);

        if (vehicleData) {
          const vList = vehicleData?.vehicles || [];
          setTotalVehicles(vList.filter(v => v.status === 'Active' || !v.status).length || vList.length);
        }

        setPendingLeaves(leavesData?.leaves || []);
        setUpcomingHolidays(
          (holidayData?.holidays || [])
            .filter(h => dayjs(h.date).isSame(dayjs(), 'day') || dayjs(h.date).isAfter(dayjs()))
            .slice(0, 5)
        );
        setSideLoading(false);

        if (!sections.length || !acYear) {
          setStatsLoading(false);
          setDeptLoading(false);
          setFeesLoading(false);
          setPageReady(true);
          return;
        }

        // Step 2: Load attendance + ALL students (one call) + fees structure only
        // getFeesTotal is called lazily after page shows (it runs 3 DB queries per section internally)
        const formatted = dayjs(selectedDate).format('DD/MM/YYYY');
        const isAdmin = user?.role !== 'Teacher';

        const [attendanceRes, allStudentsRes, structRes] = await Promise.all([
          InvokeApi(Config.main_url + apiList.getAttendanceByDate, { date: formatted }),
          InvokeApi(Config.main_url + apiList.getStudents, { academic_year: acYear, status: 'Active' }),
          isAdmin
            ? InvokeApi(Config.main_url + apiList.getFeesStructureMain, { status: 'Active', academic_year: acYear })
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        const allStudents = allStudentsRes?.students || [];
        const sectionStudentMap = {};
        allStudents.forEach(s => {
          const key = `${s.standard?.trim()}-${s.section?.trim()}`;
          sectionStudentMap[key] = (sectionStudentMap[key] || 0) + 1;
        });

        // ── Attendance stats — total from sectionStudentMap (reliable single call)
        const attendance = attendanceRes?.attendance || [];
        const present = attendance.filter(a => a.status === 'Present').length;
        const absent  = attendance.filter(a => a.status === 'Absent').length;
        const leave   = attendance.filter(a => a.status === 'Leave').length;
        const total   = allStudents.length; // direct count — no per-section errors

        setStats({ present, absent, leave, total });

        const deptResults = sections.map(d => {
          const deptAtt = attendance.filter(a => a.standard === d.standard && a.section === d.section);
          const p  = deptAtt.filter(a => a.status === 'Present').length;
          const ab = deptAtt.filter(a => a.status === 'Absent').length;
          const lv = deptAtt.filter(a => a.status === 'Leave').length;
          return { standard: d.standard, section: d.section, present: p, absent: ab, leave: lv, total: p + ab + lv };
        });
        setDeptStats(deptResults.filter(d => d.total > 0));
        setStatsLoading(false);
        setDeptLoading(false);

        // ── Fees stats: use structure data + student counts only for initial display
        // Actual paid amounts loaded lazily to avoid 24+ DB queries on load
        if (isAdmin) {
          const allStructures = structRes?.feesstructure || [];
          const feesResults = sections.map(d => {
            const key           = `${d.standard.trim()}-${d.section.trim()}`;
            const studentCount  = sectionStudentMap[key] || 0;
            const structure     = allStructures.find(
              s => s.standard?.trim() === d.standard?.trim() && s.section?.trim() === d.section?.trim()
            );
            const feePerStudent = parseFloat(structure?.total_fees || 0);
            const totalFees     = feePerStudent * studentCount;
            return { standard: d.standard, section: d.section, totalFees, totalFeesPaid: 0, totalFeesBalance: totalFees, students: studentCount };
          });
          setFeesStats(feesResults);
          setTotalFeesAmount(feesResults.reduce((s, r) => s + (r.totalFees || 0), 0));
          setTotalFeesPaid(0); // will be updated after lazy load below
        }
        setFeesLoading(false);

      } catch (err) {
        setStatsLoading(false);
        setDeptLoading(false);
        setFeesLoading(false);
        setSideLoading(false);
      } finally {
        if (!cancelled) setPageReady(true);
      }
    };

    loadAll();
    return () => { cancelled = true; };
  }, [user?.role]);

  // ── Lazy fees totals — ONE call for all sections via getFeesTotalAll ─────────
  useEffect(() => {
    if (!pageReady || !depts.length || !currentAcYear || user?.role === 'Teacher') return;
    let cancelled = false;
    const loadFeesPaid = async () => {
      try {
        // Single SQL query on backend — returns paid totals for ALL sections at once
        const res = await InvokeApi(Config.main_url + apiList.getFeesTotalAll, {
          academic_year: currentAcYear,
        });
        if (cancelled) return;

        const feeRows = res?.fees || [];
        // Build a lookup map: "standard-section" → { totalFees, totalFeesPaid }
        const feeMap = {};
        feeRows.forEach(r => {
          feeMap[`${r.standard?.trim()}-${r.section?.trim()}`] = {
            totalFees: parseFloat(r.totalFees || 0),
            totalFeesPaid: parseFloat(r.totalFeesPaid || 0)
          };
        });

        let aggPaid = 0;
        let aggTotal = 0;
        setFeesStats(prev => prev.map(f => {
          const key     = `${f.standard?.trim()}-${f.section?.trim()}`;
          const feeData = feeMap[key] ?? { totalFees: f.totalFees, totalFeesPaid: 0 };
          const paid    = feeData.totalFeesPaid;
          const total   = feeData.totalFees;
          const balance = Math.max(0, total - paid);
          aggPaid += paid;
          aggTotal += total;
          return { ...f, totalFees: total, totalFeesPaid: paid, totalFeesBalance: balance };
        }));
        setTotalFeesPaid(aggPaid);
        setTotalFeesAmount(aggTotal);
      } catch (_) {}
    };
    loadFeesPaid();
    return () => { cancelled = true; };
  }, [pageReady, depts, currentAcYear, user?.role]);

  // ── loadStats: only for date change / manual refresh ────────────────────────
  const loadStats = useCallback(async () => {
    if (!depts.length) return;
    setStatsLoading(true);
    setDeptLoading(true);

    const formatted = dayjs(selectedDate).format('DD/MM/YYYY');
    const [allAttendance, allStudentsRes] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getAttendanceByDate, { date: formatted }),
      InvokeApi(Config.main_url + apiList.getStudents, { academic_year: currentAcYear, status: 'Active' }),
    ]);

    const attendance  = allAttendance?.attendance || [];
    const allStudents = allStudentsRes?.students || [];
    const present = attendance.filter(a => a.status === 'Present').length;
    const absent  = attendance.filter(a => a.status === 'Absent').length;
    const leave   = attendance.filter(a => a.status === 'Leave').length;
    const total   = allStudents.length;
    setStats({ present, absent, leave, total });
    setStatsLoading(false);

    const deptResults = depts.map(d => {
      const deptAtt = attendance.filter(a => a.standard === d.standard && a.section === d.section);
      const p  = deptAtt.filter(a => a.status === 'Present').length;
      const ab = deptAtt.filter(a => a.status === 'Absent').length;
      const lv = deptAtt.filter(a => a.status === 'Leave').length;
      return { standard: d.standard, section: d.section, present: p, absent: ab, leave: lv, total: p + ab + lv };
    });
    setDeptStats(deptResults.filter(d => d.total > 0));
    setDeptLoading(false);
  }, [depts, selectedDate, currentAcYear]);

  // Re-run attendance when date changes (after initial load)
  useEffect(() => {
    if (pageReady) loadStats();
  }, [selectedDate]);

  const isToday = selectedDate === dayjs().format('YYYY-MM-DD');

  // ── Full-screen loader — show until ALL data is ready ─────────────────────
  if (!pageReady) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: '80vh', gap: 2,
      }}>
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
        <Typography variant="body1" color="text.secondary" fontWeight={600}>
          Loading Dashboard...
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Fetching attendance, fees and class data
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={900} color="#1a237e">
            St. Lord's International Public School
          </Typography>
          {/* <Typography variant="body2" color="text.secondary" mt={0.5}>
            Role: <strong>{user?.role}</strong> · {dayjs().format('dddd, MMMM D, YYYY')}
          </Typography> */}
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Management Panel
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type="date" size="small" value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label="Select Date"
            sx={{ minWidth: 170 }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={loadStats} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={isToday ? ' Today' : ` ${dayjs(selectedDate).format('DD MMM YYYY')}`}
          sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }}
        />
        {/* {!isToday && (
          <Chip label="Click date picker to change" size="small" variant="outlined"
            sx={{ color: 'text.secondary', borderColor: '#e0e0e0', fontSize: 11 }} />
        )} */}
      </Box>

      {/* ── Stat Cards — 4 per row, fixed width ── */}
      <Grid container spacing={2} mb={3}>
        {[
          { title: 'Total Students', value: stats.total,   icon: <People />,      gradient: 'linear-gradient(135deg,#1a237e,#283593)', sub: 'Active' },
          { title: 'Present',         value: stats.present, icon: <CheckCircle />, gradient: 'linear-gradient(135deg,#2e7d32,#388e3c)', sub: 'Checked in',    nav: '/attendance' },
          { title: 'Absent',          value: stats.absent,  icon: <Cancel />,      gradient: 'linear-gradient(135deg,#c62828,#d32f2f)', sub: 'Not checked in', nav: '/attendance' },
          // Fees Collected — Admin only
          ...(user?.role !== 'Teacher' ? [{
            title: 'Fees Collected',
            value: fmtINR(totalFeesPaid),
            icon: <AttachMoney />,
            gradient: 'linear-gradient(135deg,#6a1b9a,#7b1fa2)',
            sub: totalFeesAmount > 0
              ? `${Math.round((totalFeesPaid / totalFeesAmount) * 100)}% of ${fmtINR(totalFeesAmount)} · ${Math.round(100 - (totalFeesPaid / totalFeesAmount) * 100)}% pending`
              : 'No fee structure set',
            nav: '/fees-structure',
          }] : []),
          { title: 'On Leave',        value: stats.leave,   icon: <BeachAccess />, gradient: 'linear-gradient(135deg,#e65100,#f57c00)', sub: 'Approved',       nav: '/attendance' },
          { title: 'Active Vehicles', value: totalVehicles, icon: <DirectionsBus />, gradient: 'linear-gradient(135deg,#00695c,#00796b)', sub: 'School buses',   nav: '/vehicles' },
          { title: 'Pending Leaves',  value: pendingLeaves.length, icon: <HourglassEmpty />, gradient: 'linear-gradient(135deg,#37474f,#546e7a)', sub: 'Awaiting approval', nav: '/leave' },
          { title: 'Departments',     value: depts.length,  icon: <Business />,    gradient: 'linear-gradient(135deg,#0277bd,#0288d1)', sub: 'Active depts',   nav: '/departments' },
        ].map(s => (
          <Grid item xs={6} sm={3} md={3} key={s.title}>
            <StatCard title={s.title} value={s.value} icon={s.icon} gradient={s.gradient}
              sub={s.sub} loading={statsLoading} onClick={s.nav ? () => navigate(s.nav) : undefined} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1a237e">Class Attendance</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(selectedDate).format('DD MMM YYYY')} · Present, Absent, Leave
                  </Typography>
                </Box>
                <Chip label={`${deptStats.length} class${deptStats.length !== 1 ? 'es' : ''}`} size="small"
                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
              </Box>

              {deptLoading ? (
                [...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ mb: 2.5 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="rectangular" height={7} sx={{ borderRadius: 4, mt: 0.8 }} />
                  </Box>
                ))
              ) : deptStats.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Business sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>No attendance data for this date</Typography>
                  <Typography variant="caption" color="text.secondary">Try selecting a different date</Typography>
                </Box>
              ) : (
                deptStats.map(d => (
                  <DeptBar key={`${d.standard}-${d.section}`} std={d.standard} sec={d.section} present={d.present} absent={d.absent} leave={d.leave} total={d.total} />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {user?.role !== 'Teacher' && (
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1a237e">Fees Collection</Typography>
                  <Typography variant="caption" color="text.secondary">{currentAcYear} · Paid vs Pending</Typography>
                </Box>
                <Chip label={`${feesStats.length} class${feesStats.length !== 1 ? 'es' : ''}`} size="small"
                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
              </Box>

              {feesLoading ? (
                [...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ mb: 2.5 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="rectangular" height={7} sx={{ borderRadius: 4, mt: 0.8 }} />
                  </Box>
                ))
              ) : feesStats.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Business sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>No classes found</Typography>
                  <Typography variant="caption" color="text.secondary">Add classes in Departments section</Typography>
                </Box>
              ) : (
                feesStats.map(f => (
                  <FeesBar key={`${f.standard}-${f.section}`} std={f.standard} sec={f.section}
                    totalFees={f.totalFees} totalFeesPaid={f.totalFeesPaid} totalFeesBalance={f.totalFeesBalance}
                    students={f.students || 0} />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Grid container spacing={3} direction="column">

            <Grid item>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1a237e">Pending Leaves</Typography>
                      <Typography variant="caption" color="text.secondary">Awaiting approval</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {pendingLeaves.length > 0 && (
                        <Chip label={pendingLeaves.length} size="small"
                          sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                      )}
                      <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/leave')}
                        sx={{ color: '#1a237e', fontWeight: 600, fontSize: 11, minWidth: 0 }}>
                        All
                      </Button>
                    </Box>
                  </Box>

                  {sideLoading ? (
                    [...Array(3)].map((_, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="70%" />
                          <Skeleton variant="text" width="50%" />
                        </Box>
                      </Box>
                    ))
                  ) : pendingLeaves.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <BeachAccess sx={{ fontSize: 36, color: '#e8eaf6', mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">No pending requests</Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {pendingLeaves.slice(0, 4).map((l, i) => (
                        <React.Fragment key={l.ID || i}>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#e65100,#fb8c00)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                                {(l.student_name || 'S')[0]}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography fontWeight={600} fontSize={13}>{l.student_name || `Student #${l.user_id}`}</Typography>}
                              secondary={
                                <Typography fontSize={11} color="text.secondary">
                                  {l.leave_type} · {l.start_date ? dayjs(l.start_date).format('DD MMM') : '—'} → {l.end_date ? dayjs(l.end_date).format('DD MMM') : '—'}
                                </Typography>
                              }
                            />
                            <Chip label="Pending" size="small"
                              sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: 10 }} />
                          </ListItem>
                          {i < Math.min(pendingLeaves.length, 4) - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1a237e">Upcoming Holidays</Typography>
                      <Typography variant="caption" color="text.secondary">Next scheduled holidays</Typography>
                    </Box>
                    <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/holidays')}
                      sx={{ color: '#1a237e', fontWeight: 600, fontSize: 11, minWidth: 0 }}>
                      All
                    </Button>
                  </Box>

                  {sideLoading ? (
                    [...Array(3)].map((_, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                        <Skeleton variant="rounded" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="70%" />
                          <Skeleton variant="text" width="40%" />
                        </Box>
                      </Box>
                    ))
                  ) : upcomingHolidays.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <CalendarMonth sx={{ fontSize: 36, color: '#e8eaf6', mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">No upcoming holidays</Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {upcomingHolidays.map((h, i) => {
                        const daysLeft = dayjs(h.date).diff(dayjs(), 'day');
                        return (
                          <React.Fragment key={h.ID || i}>
                            <ListItem disablePadding sx={{ py: 1 }}>
                              <ListItemAvatar>
                                <Avatar sx={{
                                  width: 42, height: 42, borderRadius: 2,
                                  background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                                  flexDirection: 'column',
                                }}>
                                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 900, lineHeight: 1 }}>
                                    {dayjs(h.date).format('DD')}
                                  </Typography>
                                  <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 9, lineHeight: 1 }}>
                                    {dayjs(h.date).format('MMM')}
                                  </Typography>
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Typography fontWeight={600} fontSize={13}>{h.title}</Typography>}
                                secondary={<Typography fontSize={11} color="text.secondary">{dayjs(h.date).format('dddd, YYYY')}</Typography>}
                              />
                              <Chip
                                label={daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d`}
                                size="small"
                                sx={{
                                  background: daysLeft === 0 ? '#e8f5e9' : daysLeft <= 3 ? '#fff3e0' : '#e8eaf6',
                                  color: daysLeft === 0 ? '#2e7d32' : daysLeft <= 3 ? '#e65100' : '#1a237e',
                                  fontWeight: 700, fontSize: 10,
                                }}
                              />
                            </ListItem>
                            {i < upcomingHolidays.length - 1 && <Divider />}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
